import { Box, useTheme, alpha } from '@mui/material'

import { Dispatch, SetStateAction, useState } from 'react'
import { IFilter } from 'src/components/filter/types/filter.types'
import { EScanStatus, IScansStatusCount } from 'src/types'

export interface IProps {
  statusCountInfo: IScansStatusCount | undefined | void
  filterName: string
  defaultValue: string[]
  setFilter: Dispatch<SetStateAction<IFilter>>
}

export enum EStatusActive {
  NEW = 'new',
  APPROVED = 'approved',
  NOT_APPROVED = 'not_approved',
  ON_CHAIN = 'on-chain',
  NOT_ON_CHAIN = 'not_on-chain',
}

export const ScansStatusInfo = ({
  setFilter,
  filterName,
  defaultValue,
  statusCountInfo,
}: IProps) => {
  const theme = useTheme()
  const [active, setActive] = useState<EStatusActive | null>(null)

  const handleFilter = (status: EScanStatus[], activeName: EStatusActive) => {
    if (activeName === active) {
      setActive(null)
      setFilter((prevFilter) => ({ ...prevFilter, [filterName]: defaultValue }))
    } else {
      setActive(activeName)
      setFilter((prevFilter) => ({ ...prevFilter, [filterName]: status }))
    }
  }

  if (!statusCountInfo) {
    return null
  }

  const notOnChainCount =
    statusCountInfo.approved + statusCountInfo.not_approved + statusCountInfo.new

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: '5px',
        fontSize: '14px',
        px: '4px',
      }}
    >
      {statusCountInfo.new > 0 && (
        <Box
          sx={{
            cursor: 'pointer',
            color:
              active === EStatusActive.NEW
                ? theme.palette.custom.warning
                : alpha(theme.palette.custom.warning, 0.7),
            borderBottom: `1px solid ${theme.palette.custom.white}`,
            transition: 'color 0.3s ease-in-out, border-color 0.3s ease-in-out',
            '&:hover': {
              color: theme.palette.custom.warning,
              borderBottom: `1px solid ${theme.palette.custom.warning}`,
            },
          }}
          onClick={handleFilter.bind(null, [EScanStatus.NEW], EStatusActive.NEW)}
        >
          {`There are ${statusCountInfo.new} New Scans to Review`}
        </Box>
      )}
      {statusCountInfo.approved > 0 && (
        <Box
          sx={{
            cursor: 'pointer',
            color:
              active === EStatusActive.APPROVED
                ? theme.palette.custom.black
                : alpha(theme.palette.custom.black, 0.6),
            borderBottom: `1px solid ${theme.palette.custom.white}`,
            transition: 'color 0.3s ease-in-out, border-color 0.3s ease-in-out',
            '&:hover': {
              color: theme.palette.custom.black,
              borderBottom: `1px solid ${theme.palette.custom.black}`,
            },
          }}
          onClick={handleFilter.bind(null, [EScanStatus.APPROVED], EStatusActive.APPROVED)}
        >
          {`There are ${statusCountInfo.approved} Scans Approved`}
        </Box>
      )}
      {statusCountInfo.not_approved > 0 && (
        <Box
          sx={{
            cursor: 'pointer',
            color:
              active === EStatusActive.NOT_APPROVED
                ? theme.palette.custom.black
                : alpha(theme.palette.custom.black, 0.6),
            borderBottom: `1px solid ${theme.palette.custom.white}`,
            transition: 'color 0.3s ease-in-out, border-color 0.3s ease-in-out',
            '&:hover': {
              color: theme.palette.custom.black,
              borderBottom: `1px solid ${theme.palette.custom.black}`,
            },
          }}
          onClick={handleFilter.bind(null, [EScanStatus.NOT_APPROVED], EStatusActive.NOT_APPROVED)}
        >
          {`There are ${statusCountInfo.not_approved} Scans Declined`}
        </Box>
      )}
      {statusCountInfo['on-chain'] > 0 && (
        <Box
          sx={{
            cursor: 'pointer',
            color:
              active === EStatusActive.ON_CHAIN
                ? theme.palette.custom.black
                : alpha(theme.palette.custom.black, 0.6),
            borderBottom: `1px solid ${theme.palette.custom.white}`,
            transition: 'color 0.3s ease-in-out, border-color 0.3s ease-in-out',
            '&:hover': {
              color: theme.palette.custom.black,
              borderBottom: `1px solid ${theme.palette.custom.black}`,
            },
          }}
          onClick={handleFilter.bind(null, [EScanStatus.ON_CHAIN], EStatusActive.ON_CHAIN)}
        >
          {`There are ${statusCountInfo['on-chain']} Scans On-Chain`}
        </Box>
      )}
      {notOnChainCount > 0 && (
        <Box
          sx={{
            cursor: 'pointer',
            color:
              active === EStatusActive.NOT_ON_CHAIN
                ? theme.palette.custom.black
                : alpha(theme.palette.custom.black, 0.6),
            borderBottom: `1px solid ${theme.palette.custom.white}`,
            transition: 'color 0.3s ease-in-out, border-color 0.3s ease-in-out',
            '&:hover': {
              color: theme.palette.custom.black,
              borderBottom: `1px solid ${theme.palette.custom.black}`,
            },
          }}
          onClick={handleFilter.bind(
            null,
            [EScanStatus.NEW, EScanStatus.APPROVED, EScanStatus.NOT_APPROVED],
            EStatusActive.NOT_ON_CHAIN,
          )}
        >
          {`There are ${notOnChainCount} Scans not On-Chain`}
        </Box>
      )}
    </Box>
  )
}
